import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaEnvelope,
  FaUserTie,
  FaSchool,
} from "react-icons/fa";
import useScrollToTop from "./../../hooks/useScollToTop";
import backgroundImage from "./illustration_login.webp";
import {
  registerTeacher,
  validateInvitation,
} from "../../services/invitationServices";

function TeacherRegisterPage() {
  useScrollToTop();
  const { token } = useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [clubName, setClubName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isCheckingInvitation, setIsCheckingInvitation] = useState(true);
  const [isInvitationValid, setIsInvitationValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        setIsCheckingInvitation(true);
        setErrorMessage("");

        if (!token) {
          setIsInvitationValid(false);
          setErrorMessage("Invitation invalide ou expirée.");
          return;
        }

        const data = await validateInvitation(token);

        if (data) {
          setEmail(data.email || "");
          setClubName(data.club_name || "");
          setIsInvitationValid(true);
        } else {
          setIsInvitationValid(false);
          setErrorMessage("Invitation invalide ou expirée.");
        }
      } catch (error) {
        setIsInvitationValid(false);
        setErrorMessage("Impossible de vérifier l'invitation.");
      } finally {
        setIsCheckingInvitation(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!token) {
      setErrorMessage("Token invalide ou inexistant.");
      return;
    }

    if (!isInvitationValid) {
      setErrorMessage("Cette invitation n'est pas valide.");
      return;
    }

    if (!password) {
      setErrorMessage("Veuillez choisir un mot de passe.");
      return;
    }

    if (password.length < 6) {
      setErrorMessage("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await registerTeacher(token, password);

      if (result) {
        const successText =
          "Votre compte enseignant a été créé avec succès. Vous pouvez maintenant vous connecter.";
        setSuccessMessage(successText);

        setTimeout(() => {
          navigate("/login", {
            state: { successMessage: successText },
          });
        }, 1600);
      } else {
        setErrorMessage("Erreur lors de l'inscription.");
      }
    } catch (error) {
      setErrorMessage("Erreur lors de l'inscription.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-slate-950"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

      <div className="relative z-10 flex min-h-screen items-start justify-center px-3 py-3 sm:px-4 sm:py-5 md:items-center md:py-8">
        <div className="w-full max-w-[980px] overflow-hidden rounded-2xl border border-white/10 bg-white/95 shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95 md:rounded-3xl">
          <div className="grid md:grid-cols-[0.88fr_1.12fr]">
            {/* Colonne branding desktop */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white lg:p-8">
              <div>
                <p className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Présence Club Pro
                </p>

                <h1 className="text-2xl font-black leading-tight lg:text-3xl">
                  Finalisez votre invitation enseignant
                </h1>

                <p className="mt-5 max-w-md text-sm leading-6 text-white/80">
                  Créez votre accès enseignant pour rejoindre votre club et
                  commencer à utiliser l’application.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Invitation liée directement à votre adresse email.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Création rapide de votre accès enseignant.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Connexion possible immédiatement après validation.
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne contenu */}
            <div className="p-4 sm:p-6 md:p-7 lg:p-8">
              <div className="mx-auto w-full max-w-none">
                {/* Intro mobile */}
                <div className="mb-5 md:hidden">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 text-white shadow-lg backdrop-blur-sm">
                    <p className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                      Présence Club Pro
                    </p>

                    <h1 className="text-xl font-black leading-tight">
                      Finalisez votre invitation enseignant
                    </h1>

                    <p className="mt-2 text-sm leading-6 text-white/80">
                      Créez votre accès enseignant pour rejoindre votre club.
                    </p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Invitation sécurisée liée à votre email.
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Accès rapide à votre espace enseignant.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5 text-center md:text-left">
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">
                    Inscription enseignant
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Finalisez votre inscription à partir de votre invitation.
                  </p>
                </div>

                {successMessage && (
                  <div
                    className="mb-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300"
                    role="alert"
                  >
                    {successMessage}
                  </div>
                )}

                {errorMessage && (
                  <div
                    className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20 dark:bg-blue-500">
                        <FaUserTie />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          Création du compte enseignant
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Votre invitation détermine votre email et votre club.
                        </p>
                      </div>
                    </div>

                    {isCheckingInvitation ? (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        Vérification de l'invitation...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Club invité
                          </label>
                          <div className="relative">
                            <FaSchool className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-slate-200 bg-slate-100 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                              value={clubName}
                              disabled
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Email
                          </label>
                          <div className="relative">
                            <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type="email"
                              className="w-full rounded-2xl border border-slate-200 bg-slate-100 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                              value={email}
                              disabled
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Mot de passe
                          </label>
                          <div className="relative">
                            <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type={showPassword ? "text" : "password"}
                              placeholder="Choisissez un mot de passe"
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              autoComplete="new-password"
                              minLength={6}
                              required
                              disabled={!isInvitationValid || isSubmitting}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((prev) => !prev)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                              aria-label={
                                showPassword
                                  ? "Masquer le mot de passe"
                                  : "Afficher le mot de passe"
                              }
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Minimum 6 caractères.
                          </p>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Confirmez le mot de passe
                          </label>
                          <div className="relative">
                            <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Confirmez votre mot de passe"
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              autoComplete="new-password"
                              minLength={6}
                              required
                              disabled={!isInvitationValid || isSubmitting}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword((prev) => !prev)
                              }
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                              aria-label={
                                showConfirmPassword
                                  ? "Masquer la confirmation"
                                  : "Afficher la confirmation"
                              }
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-600"
                    type="submit"
                    disabled={
                      isCheckingInvitation || !isInvitationValid || isSubmitting
                    }
                  >
                    {isSubmitting
                      ? "Création du compte..."
                      : "Créer mon compte enseignant"}
                  </button>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Link
                        to="/login"
                        className="font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Retour à la connexion
                      </Link>

                      <button
                        type="button"
                        onClick={() => navigate("/register")}
                        className="text-left font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 sm:text-right"
                      >
                        Créer un compte classique
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeacherRegisterPage;